import { ImageScaleView, MovieClip, View } from '@play-co/timestep-core/ui';
import { ButtonScaleViewOpts } from 'src/lib/ui/components/ButtonScaleView';
import StateObserver, { now, user } from 'src/StateObserver';
import * as Match2 from 'src/replicant/match2';
import { type Match2DailyChallengeId } from 'src/replicant/match2/dailychallenge/match2-dailychallenge.ruleset';
import { FeatureButton } from 'src/game/components/FeatureButton';
import { AssetGroup } from '@play-co/timestep-core';
import { createEmitter } from 'src/lib/Emitter';
import { shouldSyncDailyChallenge } from 'src/replicant/match2/dailychallenge/match2-dailychallenge.getters';
import { getMatch2Level } from 'src/replicant/match2/match2.getters';
import { State } from 'src/replicant/State';

const assets = {
  monday: {
    movieclip: {
      url: 'assets/ui/match2/daily_challenges/monday/button',
      loadMethod: MovieClip.animationLoader,
    },
  },
  tuesday: {
    movieclip: {
      url: 'assets/ui/match2/daily_challenges/tuesday/button',
      loadMethod: MovieClip.animationLoader,
    },
  },
  wednesday: {
    movieclip: {
      url: 'assets/ui/match2/daily_challenges/wednesday/button',
      loadMethod: MovieClip.animationLoader,
    },
  },
  thursday: {
    movieclip: {
      url: 'assets/ui/match2/daily_challenges/thursday/button',
      loadMethod: MovieClip.animationLoader,
    },
  },
  friday: {
    movieclip: {
      url: 'assets/ui/match2/daily_challenges/friday/button',
      loadMethod: MovieClip.animationLoader,
    },
  },
  saturday: {
    image: 'assets/ui/match2/daily_challenges/buttons/button_saturday.png',
  },
  sunday: {
    image: 'assets/ui/match2/daily_challenges/buttons/button_sunday.png',
  },
};

type AnimationState = keyof typeof assets;

export class Match2DailyChallengeButtonAnimated extends FeatureButton<AnimationState> {
  private badge: ImageScaleView;

  public override getElementsToApplyFilter() {
    const elements: View[] = [this.badge];
    if (this.movieclip) elements.push(this.movieclip);
    return elements;
  }

  constructor(opts: ButtonScaleViewOpts) {
    super({
      width: 154,
      height: 160,
      pressedOffsetY: 5,
      labelPanel: {
        fontSize: 23,
        offsetY: -25,
      },
      ...opts,
    });

    this.badge = new ImageScaleView({
      superview: this.container,
      x: this.style.width * 0.5,
      y: 80,
      width: 154,
      height: 154,
      centerOnOrigin: true,
      scaleMethod: 'contain',
    });

    this.animationStateMap = {
      monday: {
        type: 'movieclip',
        assetGroup: new AssetGroup(Object.values(assets.monday)),
        url: assets.monday.movieclip.url,
        sequence: ['waterballoon_spawn', 'waterballoon_loop'],
        scale: 0.65,
        onEnter: this.refresh.bind(this),
      },
      tuesday: {
        type: 'movieclip',
        assetGroup: new AssetGroup(Object.values(assets.tuesday)),
        url: assets.tuesday.movieclip.url,
        sequence: ['movelikeburuburu_spawn', 'movelikeburuburu_loop'],
        scale: 0.65,
        onEnter: this.refresh.bind(this),
      },
      wednesday: {
        type: 'movieclip',
        assetGroup: new AssetGroup(Object.values(assets.wednesday)),
        url: assets.wednesday.movieclip.url,
        sequence: ['lollipop_spawn', 'lollipop_loop'],
        scale: 0.65,
        onEnter: this.refresh.bind(this),
      },
      thursday: {
        type: 'movieclip',
        assetGroup: new AssetGroup(Object.values(assets.thursday)),
        url: assets.thursday.movieclip.url,
        sequence: ['burustreak_spawn', 'burustreak_loop'],
        scale: 0.65,
        onEnter: this.refresh.bind(this),
      },
      friday: {
        type: 'movieclip',
        assetGroup: new AssetGroup(Object.values(assets.friday)),
        url: assets.friday.movieclip.url,
        sequence: ['yellowfever_spawn', 'yellowfever_loop'],
        scale: 0.65,
        onEnter: this.refresh.bind(this),
      },
      saturday: {
        // This will display the static image only
        type: 'custom',
        assetGroup: new AssetGroup(Object.values(assets.saturday)),
      },
      sunday: {
        // This will display the static image only
        type: 'custom',
        assetGroup: new AssetGroup(Object.values(assets.sunday)),
      },
    };

    this.setNotification({
      condition: (user) => this.canShowNotification(user),
      positioning: { align: 'right' },
    });

    // Refresh button state on challenge availability/progress/claim changes
    // Also does an initial state refresh
    createEmitter(this, ({ user }) => ({
      level: getMatch2Level(user),
      available: Match2.dailyChallengeGetters.isDailyChallengeAvailable(user),
      progress: user.match2.dailyChallenge?.progress,
      claimed: !!user.match2.dailyChallenge?.rewardsClaimed,
    })).addListener(() => this.refresh());
  }

  private canShowNotification(user: State) {
    // Do not show if button is locked
    if (this.isLocked) return false;

    // Show if daily challenge is complete and ready to be claimed
    return Match2.dailyChallengeGetters.isDailyChallengeComplete(user);
  }

  public async update() {
    // Make sure that the daily challenge state is up-to-date
    if (shouldSyncDailyChallenge(user(), now())) {
      await StateObserver.invoke.syncMatch2DailyChallenge();
      this.refresh();
    }
  }

  private refresh() {
    const { user } = StateObserver.getState();

    // Hide if daily challenge is not available
    if (!Match2.dailyChallengeGetters.isDailyChallengeAvailable(user)) {
      this.hide();
      return;
    }

    const config = Match2.dailyChallengeGetters.getDailyChallengeConfig(user);

    // Config not found, hide the button instead of throwing an error
    // Seems that can happen if the daily challenge is available but
    // there is no config matching the current match2 level
    if (!config) {
      this.hide();
      return;
    }

    const id = Match2.dailyChallengeGetters.getDailyChallengeId(user);

    // Challenge id not found, hide the button instead of throwing an error
    if (!id) {
      this.hide();
      return;
    }

    const completed =
      Match2.dailyChallengeGetters.isDailyChallengeComplete(user);
    const claimed = !!user.match2.dailyChallenge?.rewardsClaimed;

    // Hide if rewards have been claimed
    if (claimed) {
      this.hide();
      return;
    }

    // Otherwise, update the button state accordingly
    this.show();
    this.updateImage(id);

    if (completed) {
      this.setLabelLocaleText('match2.dailyBonus.collect');
    } else if (config.requirement > 0) {
      const progress = user.match2.dailyChallenge?.progress ?? 0;
      const percent = Math.round((progress / config.requirement) * 100);
      this.setLabelText(`${percent}%`);
    }
  }

  private updateImage(id: Match2DailyChallengeId) {
    this.setAnimationState(id);

    const config = assets[id] as { image?: string; movieclip?: unknown };

    if (config.image) {
      this.badge.show();
      this.badge.updateOpts({
        image: config.image,
      });
      this.updateLabelPanel({
        labelOffsetY: 3,
      });
    }

    if (config.movieclip) {
      this.badge.hide();
      this.updateLabelPanel({
        labelOffsetY: id === 'thursday' ? 11 : 0,
      });
    }
  }
}
